import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { avatarStyle, profileInitial } from "../shared/colors.js";
import { Profile } from "../shared/type.app.js";
import { subjectSummary } from "../shared/teacher.js";
import { ReadingBeeModal } from "./component.modal.js";
import { ReadingBeePasscode } from "./component.passcode.js";
import { StoreController } from "./controller.store.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import "./component.modal.js";
import "./component.passcode.js";

@customElement("reading-bee-profile-modal")
export class ReadingBeeProfileModal extends LitElement {
  static override styles = [
    globalStyles,
    css`
      .hero {
        display: grid;
        justify-items: center;
        text-align: center;
        gap: 0.45rem;
        margin-bottom: 1.5rem;
      }

      .avatar {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        box-shadow: var(--shadow-normal);
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 1.85rem;
        line-height: 1;
      }

      .level {
        font-size: 0.95rem;
        color: var(--color-1);
        font-weight: 700;
      }

      .bar {
        width: min(220px, 70%);
        height: 8px;
        border-radius: 999px;
        background: #2a251e;
        overflow: hidden;
      }

      .bar span {
        display: block;
        height: 100%;
        background: linear-gradient(90deg, var(--color-1), #f3d27a);
      }

      .list {
        display: grid;
        gap: 0.55rem;
      }

      .row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.7rem;
        border-radius: 16px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
        width: 100%;
        text-align: left;
      }

      .row[current] {
        border-color: var(--color-1);
      }

      .mini {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 0.72rem;
        line-height: 1;
      }

      .meta {
        display: grid;
      }

      .meta small {
        color: var(--color-primary-text-muted);
      }
    `,
  ];

  @query("reading-bee-modal") private modal!: ReadingBeeModal;
  @state() private switchingTo: string | null = null;

  constructor() {
    super();
    new StoreController(this);
  }

  override render(): TemplateResult {
    const profile = appStore.currentProfile;
    if (!profile) return html``;
    const activeSubject = profile.selectedSubject ?? "reading";
    const activeProgress = subjectSummary(profile, activeSubject);
    const progress = `${Math.min(100, activeProgress.level)}%`;
    return html`
      <reading-bee-modal @ModelClosing=${this.reset}>
        <slot name="open-button" slot="open-button"></slot>
        <div slot="body">
          ${
            this.switchingTo
              ? this.passcodeView()
              : html`
                  <div class="hero">
                    <div class="avatar" style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                      ${profileInitial(profile.name)}
                    </div>
                    <h2>${profile.name}</h2>
                    <div class="level">${activeProgress.label} · Level ${activeProgress.level}</div>
                    <div class="bar"><span style="width:${progress}"></span></div>
                  </div>
                  <h2>Switch profile</h2>
                  <div class="list">
                    ${appStore.state.profiles.map(
                      (item) => html`
                        <button class="row" ?current=${item.id === profile.id} @click=${() => this.requestSwitch(item)}>
                          <div class="mini" style=${avatarStyle(item.primaryColor, item.secondaryColor)}>
                            ${profileInitial(item.name)}
                          </div>
                          <div class="meta">
                            <strong>${item.name}</strong>
                            <small>
                              ${subjectSummary(item, item.selectedSubject ?? "reading").label} · Level
                              ${subjectSummary(item, item.selectedSubject ?? "reading").level}
                            </small>
                          </div>
                        </button>
                      `,
                    )}
                  </div>
                `
          }
        </div>
      </reading-bee-modal>
    `;
  }

  open(): void {
    void this.modal.open();
  }

  private passcodeView(): TemplateResult {
    return html`
      <reading-bee-passcode
        title="Switch profile"
        hint="Enter the instructor passcode"
        recoverable
        @complete=${this.onUnlock}></reading-bee-passcode>
    `;
  }

  private requestSwitch(profile: Profile): void {
    if (profile.id === appStore.state.currentProfileId) return;
    if (!appStore.isLocked()) {
      appStore.switchProfile(profile.id);
      void this.modal.close();
      return;
    }
    this.switchingTo = profile.id;
  }

  private padFrom(event: Event): ReadingBeePasscode {
    return event.currentTarget as ReadingBeePasscode;
  }

  private onUnlock = (event: Event): void => {
    const value = (event as CustomEvent<{ value: string }>).detail.value;
    if (!appStore.verifyPasscode(value)) {
      this.padFrom(event).shake();
      return;
    }
    this.finishSwitch();
  };

  private finishSwitch(): void {
    if (this.switchingTo) {
      appStore.switchProfile(this.switchingTo);
    }
    this.reset();
    void this.modal.close();
  }

  private reset = (): void => {
    this.switchingTo = null;
  };
}
